import React from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Send, Clock, Loader2, CheckCircle2, ShoppingCart, ChevronRight } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useHRDashboard } from '../../hooks/useHRDashboard';
import { useHiringDeliverable } from '../../hooks/useHiringDeliverable';

const positionStatusColor = (status, darkMode) => {
  switch (status) {
    case 'In Progress':
      return darkMode ? 'text-blue-300' : 'text-blue-600';
    case 'Pending':
      return darkMode ? 'text-yellow-300' : 'text-yellow-600';
    case 'Requested':
      return darkMode ? 'text-purple-300' : 'text-purple-600';
    case 'Completed':
      return darkMode ? 'text-green-300' : 'text-green-600';
    default:
      return darkMode ? 'text-gray-300' : 'text-gray-600';
  }
};

// The four pipeline stages a position moves through, shown as a stat row.
const PIPELINE_STAGES = [
  { key: 'requested', label: 'Requested', icon: Send, tile: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' },
  { key: 'pending', label: 'Pending', icon: Clock, tile: 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400' },
  { key: 'inProgress', label: 'In Progress', icon: Loader2, tile: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' },
  { key: 'filled', label: 'Filled', icon: CheckCircle2, tile: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' },
];

/**
 * Informative "Hiring Status" card for the HR overview tab.
 * Combines the client's hiring-service allowance, a live pipeline breakdown by
 * stage, their most recent positions, and the right call-to-action.
 */
// overrideUserId/overrideCompanyId let an internal viewer (CRM Portal View)
// show the hiring status for a specific client instead of the logged-in user.
const HRHiringStatusCard = ({ className = '', overrideUserId = null, overrideCompanyId = null }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const { loading: dataLoading, metrics: m } = useHRDashboard({ userId: overrideUserId });
  const hiring = useHiringDeliverable(overrideCompanyId);

  const statusCounts = m.activePositions.statusCounts || { requested: 0, pending: 0, inProgress: 0, filled: 0 };
  const recentPositions = m.activePositions.recentPositions || [];
  const openRoles = m.activePositions.rawValue || 0;

  const { loading: hiringLoading, hasDeliverable, limit, used, remaining } = hiring;
  const canStartHire = hasDeliverable && remaining > 0;
  const usagePct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;

  return (
    <div className={`rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-5 flex flex-col ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-center gap-2 mb-1">
        <UserPlus className="w-4 h-4 text-red-600" />
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Hiring Status</h2>
      </div>
      <p className="text-xs text-center text-gray-500 dark:text-gray-400 mb-4">
        {dataLoading
          ? 'Loading your pipeline…'
          : openRoles > 0
          ? `${openRoles} open role${openRoles === 1 ? '' : 's'} in progress`
          : 'No active searches right now'}
      </p>

      {/* Hiring service allowance */}
      {hiringLoading ? (
        <div className="h-10 rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse mb-4" />
      ) : hasDeliverable ? (
        <div className="mb-4">
          <div className="flex justify-between text-xs mb-1">
            <span className="text-gray-600 dark:text-gray-300">
              {remaining} of {limit} hiring {limit === 1 ? 'service' : 'services'} available
            </span>
            <span className="text-gray-500 dark:text-gray-400">{used} used</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700">
            <div
              className={`h-full ${usagePct >= 100 ? 'bg-red-500' : 'bg-emerald-500'}`}
              style={{ width: `${usagePct}%` }}
            />
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => navigate('/portal/billing#portal-plan')}
          className="mb-4 w-full flex items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 hover:border-red-400 hover:text-red-600 transition-colors"
        >
          <ShoppingCart className="w-3.5 h-3.5" />
          No hiring service on your plan yet — add one
        </button>
      )}

      {/* Pipeline breakdown */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        {PIPELINE_STAGES.map((stage) => {
          const Icon = stage.icon;
          const count = statusCounts[stage.key] || 0;
          return (
            <div
              key={stage.key}
              className={`rounded-lg border px-2 py-2.5 text-center ${
                darkMode ? 'border-gray-800 bg-gray-800/40' : 'border-gray-100 bg-gray-50'
              }`}
            >
              <span className={`mx-auto mb-1.5 w-7 h-7 rounded-lg flex items-center justify-center ${stage.tile}`}>
                <Icon className="w-3.5 h-3.5" />
              </span>
              <div className={`text-lg font-bold leading-none ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {dataLoading ? '—' : count}
              </div>
              <div className="mt-1 text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {stage.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent positions */}
      <div className="flex-1 mb-4">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Recent positions
          </span>
          {recentPositions.length > 0 && (
            <button
              type="button"
              onClick={() => navigate('/portal/hr/hiring')}
              className="inline-flex items-center gap-0.5 text-xs font-medium text-red-600 hover:text-red-700 hover:underline"
            >
              View all <ChevronRight className="w-3 h-3" />
            </button>
          )}
        </div>
        {dataLoading ? (
          <div className="space-y-2">
            {[0, 1].map((i) => (
              <div key={i} className="h-10 rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse" />
            ))}
          </div>
        ) : recentPositions.length > 0 ? (
          <div className="space-y-2">
            {recentPositions.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => navigate(`/portal/hr/hiring/position/${p.id}`)}
                className={`w-full flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left transition-colors ${
                  darkMode
                    ? 'border-gray-700 bg-gray-800/60 hover:border-red-800'
                    : 'border-gray-200 bg-gray-50 hover:border-red-200'
                }`}
              >
                <span className={`text-sm font-medium truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {p.title}
                </span>
                <span className={`text-xs font-semibold shrink-0 ${positionStatusColor(p.status, darkMode)}`}>
                  {p.status}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400 py-2 text-center">
            No active searches. Start a hire to build your pipeline.
          </p>
        )}
      </div>

      {/* Primary action */}
      {canStartHire ? (
        <button
          type="button"
          onClick={() => navigate('/portal/hr/hiring')}
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-red-600 hover:bg-red-700 text-white transition-colors"
        >
          <UserPlus className="w-4 h-4" />
          Start a New Hire
        </button>
      ) : (
        <button
          type="button"
          onClick={() => navigate('/portal/hr/hiring')}
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
        >
          <UserPlus className="w-4 h-4" />
          Open Hiring Hub
        </button>
      )}
    </div>
  );
};

export default HRHiringStatusCard;
